/**
 * useRoadDefects Hook
 *
 * @module roadways/hooks
 * @description Hook para gestionar reportes de defectos viales
 */

import { useState, useEffect, useCallback } from 'react';
import type { RoadDefectReport } from '../types/road-defect.types';

interface UseRoadDefectsParams {
  projectId: string;
  autoLoad?: boolean;
}

interface UseRoadDefectsReturn {
  roadDefects: RoadDefectReport[];
  isLoading: boolean;
  error: string | null;
  loadRoadDefects: () => Promise<void>;
  deleteRoadDefect: (id: string) => Promise<void>;
}

/**
 * Hook para obtener y gestionar reportes de defectos viales
 *
 * @remarks
 * Por ahora retorna datos mock. Cuando el backend esté listo,
 * reemplazar con llamadas a la API.
 */
export function useRoadDefects({
  projectId,
  autoLoad = false,
}: UseRoadDefectsParams): UseRoadDefectsReturn {
  const [roadDefects, setRoadDefects] = useState<RoadDefectReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRoadDefects = useCallback(async (): Promise<void> => {
    if (!projectId) return;

    setIsLoading(true);
    setError(null);

    try {
      // TODO: Reemplazar con llamada real a API
      // const response = await roadwaysApi.getRoadDefects(projectId);
      // setRoadDefects(response);

      // Mock data por ahora
      await new Promise(resolve => setTimeout(resolve, 500));
      setRoadDefects([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar defectos viales');
      // eslint-disable-next-line no-console
      console.error('[useRoadDefects] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // eslint-disable-next-line @typescript-eslint/require-await
  const deleteRoadDefect = useCallback(async (id: string): Promise<void> => {
    try {
      // TODO: Reemplazar con llamada real a API
      // await roadwaysApi.deleteRoadDefect(id);

      setRoadDefects(prev => prev.filter(def => def.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar defecto');
      // eslint-disable-next-line no-console
      console.error('[useRoadDefects] Error deleting:', err);
      throw err;
    }
  }, []);

  useEffect(() => {
    if (autoLoad && projectId) {
      void loadRoadDefects();
    }
  }, [projectId, autoLoad, loadRoadDefects]);

  return {
    roadDefects,
    isLoading,
    error,
    loadRoadDefects,
    deleteRoadDefect,
  };
}
